/**
 * Atomic / idempotent run settlement for permanent XP and loot.
 *
 * M5 confirmed reward table:
 * - victory  → bank provisional Class XP + completion General XP + provisional loot
 *              (+ optional run currency placeholders)
 * - death    → bank provisional Class XP only; discard run loot / completion XP
 * - abandon  → bank nothing; discard provisional XP and loot
 *
 * Calling settleRun twice for the same run is a no-op (idempotent).
 * Full UI + combat wiring lands in M5-B; this module is the data-model hook.
 *
 * OPEN: XP curves / completion amounts — see SETTLEMENT_REWARD_CONFIG.
 */

import { syncGeneralPointsFromLevel } from '../domain/generalTalents';
import {
  addItemsToInventory,
  createItemInstance,
  type ItemInstance,
} from '../domain/equipment';
import { createEmptyEquippedSlots, isKnownItemDef } from '../data/equipment';
import {
  CLASS_LEVEL_CAP,
  CLASS_LEVEL_EARNED_POINTS_CAP,
  GENERAL_LEVEL_CAP,
  SETTLEMENT_REWARD_CONFIG,
  applyInventoryToPermanent,
  inventoryFromPermanent,
  type ClassProgress,
  type GeneralProgress,
  type PermanentSave,
  type ProvisionalLootItem,
  type RunState,
  type SettlementOutcome,
  type SettlementRewardConfig,
  type XpLevelProgress,
} from './schema';

/**
 * Placeholder XP→level curve (same early-game shape as temp XP for now).
 * OPEN: replace with final General / Class curves without changing callers.
 */
export const PLACEHOLDER_XP_TO_NEXT_LEVEL: readonly number[] = [
  0,
  10,
  18,
  28,
  40,
  55,
  70,
  90,
  110,
  140,
  180,
];

export function placeholderXpToNextLevel(level: number): number {
  if (level < 1) {
    return PLACEHOLDER_XP_TO_NEXT_LEVEL[1]!;
  }
  if (level >= PLACEHOLDER_XP_TO_NEXT_LEVEL.length - 1) {
    return PLACEHOLDER_XP_TO_NEXT_LEVEL[PLACEHOLDER_XP_TO_NEXT_LEVEL.length - 1]!;
  }
  return PLACEHOLDER_XP_TO_NEXT_LEVEL[level]!;
}

function applyXp(
  progress: XpLevelProgress,
  amount: number,
  levelCap: number,
): XpLevelProgress {
  if (amount <= 0) {
    return progress;
  }
  let xp = progress.xp + amount;
  let level = progress.level;
  let guard = 0;
  while (level < levelCap && xp >= placeholderXpToNextLevel(level) && guard < 200) {
    xp -= placeholderXpToNextLevel(level);
    level += 1;
    guard += 1;
  }
  if (level >= levelCap) {
    level = levelCap;
    // OPEN: behaviour of overflow XP at cap — retain for now.
  }
  return { xp, level };
}

function syncClassPointsFromLevel(cls: ClassProgress): ClassProgress {
  // Default: one point per level after 1, capped. Achievement points stay 0.
  const earnedPoints = Math.min(
    CLASS_LEVEL_EARNED_POINTS_CAP,
    Math.max(0, cls.level - 1),
  );
  return {
    ...cls,
    earnedPoints: Math.max(cls.earnedPoints, earnedPoints),
    reservedAchievementPoints: 0,
  };
}

export function accumulateProvisionalClassXp(
  run: RunState,
  amount: number,
): RunState {
  if (run.status !== 'active') {
    throw new Error('Cannot accumulate Class XP after settlement has started');
  }
  if (amount <= 0) {
    return run;
  }
  return {
    ...run,
    provisionalClassXp: run.provisionalClassXp + amount,
  };
}

export function addProvisionalLoot(
  run: RunState,
  items: readonly ProvisionalLootItem[],
): RunState {
  if (run.status !== 'active') {
    throw new Error('Cannot add provisional loot after settlement has started');
  }
  if (items.length === 0) {
    return run;
  }
  const normalized: ProvisionalLootItem[] = [];
  for (const item of items) {
    const instanceId = item.instanceId ?? item.id;
    const defId = item.defId;
    if (!instanceId || !defId || !isKnownItemDef(defId)) {
      // Skip unknown / legacy shapeless loot — never invent defs.
      continue;
    }
    normalized.push({
      instanceId,
      defId,
      rarity: item.rarity,
      source: item.source,
      affixes: item.affixes,
      itemLevel: item.itemLevel,
    });
  }
  if (normalized.length === 0) {
    return run;
  }
  return {
    ...run,
    provisionalLoot: [...run.provisionalLoot, ...normalized],
  };
}

/**
 * Marks a finished run as ready for settlement. Does not award yet.
 * Maps dungeon victory/defeat onto settlement outcomes (abandon is explicit).
 */
export function markAwaitingSettlement(
  run: RunState,
  outcome: SettlementOutcome,
): RunState {
  if (run.status === 'settled') {
    return run;
  }
  if (run.status === 'awaiting_settlement' && run.settlement === null) {
    // Already awaiting; allow outcome to be set only if identical intent via settle.
    return { ...run, status: 'awaiting_settlement' };
  }
  return {
    ...run,
    status: 'awaiting_settlement',
    // Stash intended outcome on a transient marker without applied awards yet.
    settlement: {
      outcome,
      settlementId: `${run.runId}:${outcome}:pending`,
      settledAt: 0,
      applied: {
        generalXp: 0,
        classXp: 0,
        lootItemIds: [],
        currencyGold: 0,
        currencyMaterials: 0,
        dungeonId: run.dungeonId,
        outcome,
      },
    },
  };
}

/** Infer settlement outcome from existing dungeon result fields. */
export function outcomeFromDungeon(run: RunState): SettlementOutcome | null {
  if (run.dungeon.outcome === 'victory') {
    return 'victory';
  }
  if (run.dungeon.outcome === 'defeat') {
    return 'death';
  }
  return null;
}

export interface SettleRunResult {
  permanent: PermanentSave;
  run: RunState;
  /** True when this call was a no-op because the run was already settled. */
  alreadySettled: boolean;
}

function victoryGeneralXp(
  dungeonId: string,
  config: SettlementRewardConfig,
): number {
  return (
    config.victoryGeneralXpByDungeonId[dungeonId] ?? config.defaultVictoryGeneralXp
  );
}

/**
 * Applies permanent awards exactly once per run.
 * Safe to call again after reload — returns the same permanent totals.
 */
export function settleRun(
  permanent: PermanentSave,
  run: RunState,
  outcome?: SettlementOutcome,
  config: SettlementRewardConfig = SETTLEMENT_REWARD_CONFIG,
  now: number = Date.now(),
): SettleRunResult {
  if (run.status === 'settled' && run.settlement && run.settlement.settledAt > 0) {
    return { permanent, run, alreadySettled: true };
  }

  const resolved =
    outcome ??
    run.settlement?.outcome ??
    outcomeFromDungeon(run);
  if (!resolved) {
    throw new Error(
      'settleRun requires an explicit outcome when the dungeon is still ongoing',
    );
  }

  const settlementId = `${run.runId}:${resolved}`;

  // Defence in depth: if somehow status wasn't flipped but marker matches, skip.
  if (
    run.settlement &&
    run.settlement.settlementId === settlementId &&
    run.settlement.settledAt > 0
  ) {
    return {
      permanent,
      run: { ...run, status: 'settled' },
      alreadySettled: true,
    };
  }

  let nextPermanent: PermanentSave = {
    ...permanent,
    general: { ...permanent.general },
    classes: {
      ...permanent.classes,
      vanguard: { ...permanent.classes.vanguard },
    },
    equipment: [...permanent.equipment],
    equipped: {
      ...createEmptyEquippedSlots(),
      ...(permanent.equipped ?? {}),
    },
    currency: { ...permanent.currency },
    dungeonCompletions: { ...permanent.dungeonCompletions },
  };

  let appliedGeneralXp = 0;
  let appliedClassXp = 0;
  let appliedLootIds: string[] = [];
  let appliedGold = 0;
  let appliedMaterials = 0;

  const classId = nextPermanent.profile.activeClassId;

  if (resolved === 'victory') {
    appliedClassXp = run.provisionalClassXp;
    appliedGeneralXp = victoryGeneralXp(run.dungeonId, config);

    const classProg = applyXp(
      nextPermanent.classes[classId],
      appliedClassXp,
      CLASS_LEVEL_CAP,
    );
    nextPermanent.classes[classId] = syncClassPointsFromLevel({
      ...nextPermanent.classes[classId],
      ...classProg,
    });
    {
      const leveled = applyXp(
        nextPermanent.general,
        appliedGeneralXp,
        GENERAL_LEVEL_CAP,
      );
      nextPermanent.general = syncGeneralPointsFromLevel({
        ...nextPermanent.general,
        ...leveled,
      } satisfies GeneralProgress);
    }

    const banked: ItemInstance[] = [];
    for (const item of run.provisionalLoot) {
      const instanceId = item.instanceId ?? item.id;
      const defId = item.defId;
      if (!instanceId || !defId || !isKnownItemDef(defId)) {
        continue;
      }
      // Skip if already in permanent collection (idempotent defence).
      if (nextPermanent.equipment.some((e) => e.instanceId === instanceId)) {
        continue;
      }
      banked.push(
        createItemInstance(defId, {
          instanceId,
          rarity: item.rarity,
          acquiredAt: now,
          source: item.source ?? run.dungeonId,
          affixes: item.affixes,
          itemLevel: item.itemLevel,
        }),
      );
    }
    appliedLootIds = banked.map((i) => i.instanceId);
    if (banked.length > 0) {
      const added = addItemsToInventory(
        inventoryFromPermanent(nextPermanent),
        banked,
      );
      if (added.ok) {
        nextPermanent = applyInventoryToPermanent(nextPermanent, added.inventory);
      }
    }

    if (config.bankRunCurrencyOnVictory) {
      appliedGold = run.dungeon.rewards.gold;
      appliedMaterials = run.dungeon.rewards.materials;
      nextPermanent.currency = {
        gold: nextPermanent.currency.gold + appliedGold,
        materials: nextPermanent.currency.materials + appliedMaterials,
      };
    }

    const prev = nextPermanent.dungeonCompletions[run.dungeonId];
    nextPermanent.dungeonCompletions[run.dungeonId] = {
      clearCount: (prev?.clearCount ?? 0) + 1,
      lastClearedAt: now,
    };
  } else if (resolved === 'death') {
    appliedClassXp = run.provisionalClassXp;
    const classProg = applyXp(
      nextPermanent.classes[classId],
      appliedClassXp,
      CLASS_LEVEL_CAP,
    );
    nextPermanent.classes[classId] = syncClassPointsFromLevel({
      ...nextPermanent.classes[classId],
      ...classProg,
    });
    // Loot / general XP / currency intentionally discarded.
  } else {
    // abandon — bank nothing
    appliedClassXp = 0;
    appliedGeneralXp = 0;
  }

  const settledRun: RunState = {
    ...run,
    status: 'settled',
    // Clear provisionals after banking so a buggy second path cannot re-read them.
    provisionalClassXp: 0,
    provisionalLoot: [],
    settlement: {
      outcome: resolved,
      settlementId,
      settledAt: now,
      applied: {
        generalXp: appliedGeneralXp,
        classXp: appliedClassXp,
        lootItemIds: appliedLootIds,
        currencyGold: appliedGold,
        currencyMaterials: appliedMaterials,
        dungeonId: run.dungeonId,
        outcome: resolved,
      },
    },
  };

  return {
    permanent: nextPermanent,
    run: settledRun,
    alreadySettled: false,
  };
}
